import React, { useEffect, useMemo, useRef } from 'react';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import type { Content } from '@jwp/ott-common/types/config';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useTranslation } from 'react-i18next';
import { useContentStateStore } from '@jwp/ott-common/src/stores/ContentStateStore';
import { useNavigationStack } from '@jwp/ott-common/src/stores/NavigationStackStore';
import usePlaylist from '@jwp/ott-hooks-react/src/usePlaylist';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { useNavigate } from 'react-router';

import ShelfList from '../../containers/ShelfList/ShelfList';

import '../../styles/accessibility.scss';

const parsePlaylistIds = (input: unknown): Content[] => {
  const playlistIds = typeof input === 'string' ? input.replaceAll(/\s+/g, '').split(',') : [];

  return playlistIds?.map((id) => ({ type: 'playlist', contentId: id }));
};

const Home = ({ data }: { data?: PlaylistItem }) => {
  const { config } = useConfigStore(({ config, accessModel }) => ({ config, accessModel }), shallow);
  const navigate = useNavigate();
  
  // Use config layout for default home, or dynamically generate layout for generic hubs
  const content: Content[] = data ? parsePlaylistIds(data.playlists || data.playlist) : config?.content;
  const shouldAutoOpenFirstMedia = !data && content?.length === 1 && content[0]?.type === 'playlist';
  const demoPlaylistId = shouldAutoOpenFirstMedia ? content[0]?.contentId : undefined;
  const { data: demoPlaylistData } = usePlaylist(demoPlaylistId, {}, shouldAutoOpenFirstMedia, false);
  const firstPlaylistMedia = useMemo(() => demoPlaylistData?.playlist?.[0], [demoPlaylistData]);
  
  const { t } = useTranslation('common');
  const pageId = data?.mediaid || 'home';

  const pageState = useContentStateStore((s) => s.pages[pageId]);
  const setPageState = useContentStateStore((s) => s.setPageState);

  const { setAncestor, reset } = useNavigationStack();

  const restoredRef = useRef(false);
  const hasAutoNavigatedRef = useRef(false);

  useEffect(() => {
    if (!restoredRef.current && pageState?.scrollPosition) {
      window.scrollTo(0, pageState.scrollPosition);
      restoredRef.current = true;
    }

    const handleScroll = () => {
      setPageState(pageId, { scrollPosition: window.scrollY, from: 'Home' });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [pageId, pageState?.scrollPosition, setPageState]);

  useEffect(() => {
    reset();
    setAncestor('/');
  }, [reset, setAncestor]);

  useEffect(() => {
    if (data) {
      useContentStateStore.setState({ activeScreen: data.title });
    } else {
      useContentStateStore.setState({ click: { label: 'Home', contentId: '' } });
      useContentStateStore.setState({ activeScreen: 'Home' });
    }
  }, [data]);

  useEffect(() => {
    if (!shouldAutoOpenFirstMedia || hasAutoNavigatedRef.current || !firstPlaylistMedia) {
      return;
    }

    hasAutoNavigatedRef.current = true;
    navigate(
      mediaURL({
        id: firstPlaylistMedia.mediaid,
        title: firstPlaylistMedia.title,
        playlistId: demoPlaylistData?.feedid || demoPlaylistId,
      }),
      { replace: true },
    );
  }, [shouldAutoOpenFirstMedia, firstPlaylistMedia, navigate, demoPlaylistData?.feedid, demoPlaylistId]);

  return (
    <>
      <h1 className="hideUntilFocus">{data ? data.title : t('home')}</h1>
      {/* <ShelfList rows={content} isHubContent={!!data} pageId={pageId} /> */}
    </>
  );
};

export default Home;
